import _ from "lodash";
import React from "react";
import moment from "moment";

import { goToRelativeRoute } from "modules/Services";
import { setToastMsg } from "Login/modules/LoginModule/LoginActions";
import {
  getStatusDetails,
  extractEntityName,
  getDateList,
} from "ClassRoom/modules/utils";

import { isProjectTypeIAOrEA } from "MyCalendar/modules/Utils";
import { PROJECT_STATUS } from "Projects/config/constants";
import {
  PROJECT_TYPE_DP_EXTENDED_ESSAY,
  PROJECT_TYPE_DP_TOK_ESSAY,
  PROJECT_TYPE_DP_TOK_EXHIBITION,
  PROJECT_SUBTYPE_INTERNAL_ASSESSMENT,
  PROJECT_SUBTYPE_EXTERNAL_ASSESSMENT,
  PROJECT_TYPE_COMMUNITY,
  PROJECT_TYPE_PERSONAL,
  CURRICULUM_TYPE_MYP,
  PROJECT_DEADLINE_REMINDER,
  PROJECT_DEADLINE_REMINDER_V2,
} from "Constants/stringConstants";
import { MY_CALENDAR_CONSTANTS } from "MyCalendar/constants/stringConstants";
import { DATE_TIME_CONSTANTS } from "Constants/dateTimeConstants";

import { colors } from "@toddle-edu/ds-theme";
import {
  PpOutlined,
  InternalAssessmentOutlined,
  ExternalAssessmentOutlined,
  EeOutlined,
  TheoryofKnowledgeOutlined,
} from "@toddle-edu/ds-icons";
import {
  checkIsMomentIsoString,
  checkIsDateSame,
  formatDate,
} from "Utils/DateTimeHelpers";
import { getShouldShowCourseDisplayName } from "LearningCourses/modules/Utils";
import {
  CLASS_STREAM_TASKS,
  HEADER_TABS_EMPTY_STATE_MSG_MAPPING_V2,
  HEADER_TABS_EMPTY_STATE_MSG_MAPPING_V3,
  HEADER_BASED_TABS_MAPPING,
} from "./TodosModules/TodosConstants";

import { isActiveTabUpcomingAndTodosEnabled } from "Courses/modules/utils";
import { getItemType } from "AppComponents/ConsolidatedDeadlinesWidget/modules/Utils";
import { updateNavigationObject } from "modules/NavigationModule";
import { getUserType } from "modules/CommonSelectors";
import ACLStore from "lib/aclStore";
import { getIsStudentAssignmentFeedRevampEnabled } from "Utils/ACLStoreUtils";

const getDateTime = ({ date, sourceIsWidget }) => {
  let dateTime;
  if (moment(date, "YYYY-MM-DD HH:mm").minutes() === 0) {
    dateTime = moment(date, "YYYY-MM-DD HH:mm").format(
      `D MMM${sourceIsWidget ? "" : " YYYY"}, h a`
    );
  } else {
    dateTime = moment(date, "YYYY-MM-DD HH:mm").format(
      `D MMM${sourceIsWidget ? "" : " YYYY"}, h:mm a`
    );
  }
  return dateTime;
};

const getDateTimeV2 = ({
  date,
  sourceIsWidget,
  isTodayOrTomorrow,
  isAllDayEvent,
}) => {
  if (isAllDayEvent) {
    return moment(date, DATE_TIME_CONSTANTS.YYYY_MM_DD_HH_mm).format(
      `D MMM${sourceIsWidget ? "" : " YYYY"}`
    );
  }
  if (isTodayOrTomorrow) {
    return moment(date, DATE_TIME_CONSTANTS.YYYY_MM_DD_HH_mm).minutes() === 0
      ? moment(date, DATE_TIME_CONSTANTS.YYYY_MM_DD_HH_mm).format(`h a`)
      : moment(date, DATE_TIME_CONSTANTS.YYYY_MM_DD_HH_mm).format(`h:mm a`);
  }
  return getDateTime({ date, sourceIsWidget });
};

const timeFormatsMapping = ({
  isDeadline,
  t,
  sourceIsWidget = false,
  isAllDayEvent = false,
}) => {
  let date = isDeadline;

  const isDateTimeIsoFormat = checkIsMomentIsoString({
    dateString: isDeadline,
  });

  // if iso format then only convert to local time
  if (isDateTimeIsoFormat) {
    date = moment(isDeadline).format();
  }

  let day;
  let isTodayOrTomorrow = false;
  if (moment().isSame(moment(date, "YYYY-MM-DD"), "day")) {
    day = t("classRoom:today");
    isTodayOrTomorrow = true;
  } else if (
    moment().add(1, "day").isSame(moment(date, "YYYY-MM-DD HH:mm"), "day")
  ) {
    day = t("classRoom:tomorrow");
    isTodayOrTomorrow = true;
  } else {
    day = moment(date, "YYYY-MM-DD HH:mm").format(
      sourceIsWidget ? "dddd" : "ddd"
    );
  }

  return {
    day,
    dateTime: getDateTimeV2({
      date,
      sourceIsWidget,
      isTodayOrTomorrow,
      isAllDayEvent,
    }),
    isTodayOrTomorrow,
  };
};

const getSubtitle = (item, itemType) => {
  if (itemType === MY_CALENDAR_CONSTANTS.PROJECT_DEADLINE_EVENT) {
    return _.get(item, "deadline.description", "");
  }
  let subTitle = _.get(item, "project.title.value", "");
  if (subTitle) return subTitle;
  const projFields = _.get(item, "project.fields", []);
  const foundField = _.find(projFields, { uid: "title" });
  subTitle = foundField ? foundField?.value : _.get(projFields, "0.value", "");
  return subTitle;
};

export const getTaskDetailsObj = ({ itemType, item, t, sourceIsWidget }) => {
  const isDeadline =
    _.get(item, "assignment.deadline", null) ||
    _.get(item, "deadline.deadlineDate", null);

  const isNewProjectDeadline = _.get(item, "isProjectNewDeadline", false);

  const shouldShowCourseDisplayName = getShouldShowCourseDisplayName();

  const isAllDayEvent = _.get(item, "deadline.isDeadlineAllDay", false);
  const commonTaskDetails = {
    dueTime: isDeadline
      ? timeFormatsMapping({ isDeadline, t, sourceIsWidget, isAllDayEvent })
      : { text: t("classRoom:no_due_date") },
  };
  switch (itemType) {
    case "STUDENT_ASSIGNMENT": {
      const courseDisplayName = shouldShowCourseDisplayName
        ? _.get(
            item,
            "assignment.course.learningCourse[0].academicCourse.label"
          ) ||
          _.get(item, "assignment.course.learningCourse[0].title") ||
          _.get(item, "assignment.course.title", "")
        : _.get(item, "assignment.course.title");
      const content = _.get(item, "assignment.content", "");
      const title = extractEntityName({ content });
      return {
        title,
        subTitle: courseDisplayName,
        status: _.get(item, "status", ""),
        unreadMessageCount:
          _.get(item, "conversation.unreadMessageCount", 0) +
          _.get(item, "assignment.classDiscussion.unreadMessageCount", 0),
        ...commonTaskDetails,
        courseDisplayName,
      };
    }
    case "COMMUNITY_PROJECT":
    case "PERSONAL_PROJECT":
    case MY_CALENDAR_CONSTANTS.PROJECT_DEADLINE_EVENT: {
      const courseDisplayName = shouldShowCourseDisplayName
        ? _.get(
            item,
            "project.projectGroup.courses.edges[0].node.learningCourse[0].academicCourse.label"
          ) ||
          _.get(
            item,
            "project.projectGroup.courses.edges[0].node.learningCourse[0].title"
          ) ||
          _.get(item, "project.projectGroup.courses.edges[0].node.title", "")
        : _.get(item, "project.projectGroup.courses.edges[0].node.title", "");

      return isNewProjectDeadline
        ? {
            title: _.get(item, "task.title", ""),
            subTitle: _.get(item, "task.description", ""),
            status: _.get(item, "project.status", ""),
            dueTime: _.get(item, "task.dueOn")
              ? timeFormatsMapping({
                  isDeadline: _.get(item, "task.dueOn"),
                  t,
                  sourceIsWidget,
                  isAllDayEvent,
                })
              : { text: t("classRoom:no_due_date") },
          }
        : {
            title: _.get(item, "deadline.title", ""),
            subTitle: getSubtitle(item, itemType),
            status: _.get(item, "project.status", ""),
            unreadMessageCount: _.get(
              item,
              "project.conversation.unreadMessageCount",
              0
            ),
            ...commonTaskDetails,
            courseDisplayName,
          };
    }
    case "SERVICE_AS_ACTION": {
      const courseDisplayName = shouldShowCourseDisplayName
        ? _.get(
            item,
            "project.projectGroup.courses.edges[0].node.learningCourse[0].academicCourse.label"
          ) ||
          _.get(
            item,
            "project.projectGroup.courses.edges[0].node.learningCourse[0].title"
          ) ||
          _.get(item, "project.projectGroup.courses.edges[0].node.title", "")
        : _.get(item, "project.projectGroup.courses.edges[0].node.title", "");
      return {
        title: _.get(item, "project.title.value", ""),
        subTitle: "",
        status: _.get(item, "project.status", ""),
        unreadMessageCount: _.get(
          item,
          "project.conversation.unreadMessageCount",
          0
        ),
        ...commonTaskDetails,
        courseDisplayName,
      };
    }

    default:
      return {
        title: "",
        subTitle: "",
        status: "",
        unreadMessageCount: 0,
        ...commonTaskDetails,
        courseDisplayName: "",
      };
  }
};

export const onCardClick = ({
  itemType,
  isClickedFromWidget,
  item,
  courseIds = [],
}) => {
  return async dispatch => {
    // for routing
    const assignmentId = _.get(item, "assignment.id");
    const studentAssignmentId = _.get(item, "id");
    const projectGroupId = _.get(item, "project.projectGroup.id");
    const projectId = _.get(item, "project.id");
    const projectGroupSubType = _.get(item, "project.projectGroup.subType");
    const projectGroupType = _.get(item, "project.projectGroup.type");

    const isProjectNewDeadline = _.get(item, "isProjectNewDeadline", null);

    let route;
    switch (itemType) {
      case "STUDENT_ASSIGNMENT":
        route = `${
          isClickedFromWidget ? "../todos/" : ""
        }classroom-details/${assignmentId}/${studentAssignmentId || ""}`;
        break;
      case "COMMUNITY_PROJECT":
      case "PERSONAL_PROJECT":
      case "SERVICE_AS_ACTION":
      case "PROJECT_DEADLINE": {
        if (isProjectNewDeadline) {
          dispatch(
            updateNavigationObject({
              projectCommentBox: {
                showProjectCommentBox: true,
                commentParentEntityType: PROJECT_DEADLINE_REMINDER_V2,
                itemId: _.get(item, "task.id", null),
              },
            })
          );
        } else {
          dispatch(
            updateNavigationObject({
              projectCommentBox: {
                showProjectCommentBox: true,
                commentParentEntityType: PROJECT_DEADLINE_REMINDER,
                itemId: _.get(item, "deadline.id", null),
              },
            })
          );
        }
        if (isProjectTypeIAOrEA({ projectGroupType: projectGroupSubType })) {
          let projectGroupCourseIds = _.map(
            _.get(item, "project.projectGroup.courses.edges", []),
            ({ node: { id } }) => id
          );
          projectGroupCourseIds = _.intersection(
            projectGroupCourseIds,
            courseIds
          );
          if (_.isEmpty(projectGroupCourseIds)) return;
          route = `../courses/${_.head(
            projectGroupCourseIds
          )}/projects/${projectGroupId}/progress/${projectId}`;
        } else {
          route = `../project-group-types/${projectGroupType}/projects/${projectGroupId}/progress/${projectId}`;
        }
      }
    }
    if (route) {
      dispatch(
        goToRelativeRoute({
          route,
        })
      );
    } else {
      dispatch(setToastMsg("toastMsgs:something_went_wrong"));
    }
  };
};

export const getTaskStatusObj = ({
  itemType,
  status,
  t,
  item,
  showRevampedDeadline,
}) => {
  switch (itemType) {
    case "STUDENT_ASSIGNMENT": {
      const { label, statusColor, showStatus = true } = getStatusDetails({
        data: { status, ..._.pick(item, ["isEvaluated", "submission"]) },
        t,
      });
      return {
        statusLabel: label,
        topColor: colors.textSubtle,
        bottomColor: statusColor,
        statusHeading: t("common:status"),
        showStatus,
      };
    }

    case "PROJECT_DEADLINE":
    case "COMMUNITY_PROJECT":
    case "PERSONAL_PROJECT": {
      let bottomColor = colors.textDefault;

      if (showRevampedDeadline) {
        if (_.toLower(status) === "pending") {
          bottomColor = "var(--text-semantic-warning)";
        } else if (_.toLower(status) === "completed") {
          bottomColor = "var(--text-semantic-success)";
        }
      }
      return {
        statusLabel: t(`classRoom:${status}`),
        topColor: colors.textSubtle,
        bottomColor,
        statusHeading: showRevampedDeadline
          ? t("common:submission")
          : t("common:status"),
      };
    }

    case "SERVICE_AS_ACTION": {
      const { label } = PROJECT_STATUS[status] || {};
      return {
        statusLabel: t(label),
        topColor: colors.textSubtle,
        bottomColor: colors.textDefault,
        statusHeading: t("project:activity_status"),
      };
    }
    default:
      return {
        statusLabel: "",
        topColor: colors.textSubtle,
        bottomColor: colors.textDefault,
        statusHeading: t("common:status"),
      };
  }
};

export const getProjectDeadlineObj = ({
  itemType,
  item,
  iconSize = "small",
}) => {
  switch (getItemType(item, itemType)) {
    case PROJECT_SUBTYPE_INTERNAL_ASSESSMENT:
      return {
        icon: (
          <InternalAssessmentOutlined
            variant="on"
            size={iconSize}
            style={{ color: colors.teal300 }}
            dsVersion={"2.0"}
          />
        ),
        bgColor: colors.teal900,
      };

    case PROJECT_SUBTYPE_EXTERNAL_ASSESSMENT:
      return {
        icon: (
          <ExternalAssessmentOutlined
            variant="on"
            size={iconSize}
            style={{ color: colors.blue300 }}
            dsVersion={"2.0"}
          />
        ),
        bgColor: colors.blue900,
      };

    case PROJECT_TYPE_DP_EXTENDED_ESSAY:
      return {
        icon: (
          <EeOutlined
            variant="on"
            size={iconSize}
            style={{ color: colors.violet400 }}
            dsVersion={"2.0"}
          />
        ),
        bgColor: colors.violet900,
      };

    case PROJECT_TYPE_DP_TOK_ESSAY:
    case PROJECT_TYPE_DP_TOK_EXHIBITION:
      return {
        icon: (
          <TheoryofKnowledgeOutlined
            variant="on"
            size={iconSize}
            style={{ color: colors.yellow300 }}
            dsVersion={"2.0"}
          />
        ),
        bgColor: colors.yellow900,
      };

    default: {
      return {
        icon: (
          <PpOutlined
            variant="on"
            size={iconSize}
            style={{ color: colors.violet400 }}
            dsVersion={"2.0"}
          />
        ),
        bgColor: colors.violet900,
      };
    }
  }
};

const isProjectDeadlineIncluded = activeDropdownProjectTypesArr => {
  let shouldIncludeProjectDeadline = false;
  _.forEach(activeDropdownProjectTypesArr, project => {
    if (
      _.includes(
        [
          PROJECT_SUBTYPE_EXTERNAL_ASSESSMENT,
          PROJECT_SUBTYPE_INTERNAL_ASSESSMENT,
          PROJECT_TYPE_DP_EXTENDED_ESSAY,
          PROJECT_TYPE_DP_TOK_ESSAY,
          PROJECT_TYPE_DP_TOK_EXHIBITION,
        ],
        project
      )
    ) {
      shouldIncludeProjectDeadline = true;
    }
  });
  return shouldIncludeProjectDeadline;
};

export const updateTodosFilters = ({
  activeDropdownProjectTypesArr,
  studentTodoFilters,
  projectGroups,
  currentCurriculumProgramType,
}) => {
  const studentTodoFiltersCopy = _.cloneDeep(studentTodoFilters);
  studentTodoFiltersCopy.entityTypes = [
    MY_CALENDAR_CONSTANTS.PROJECT_DEADLINE_EVENT,
    "STUDENT_ASSIGNMENT",
  ];

  if (!_.includes(activeDropdownProjectTypesArr, CLASS_STREAM_TASKS)) {
    studentTodoFiltersCopy.entityTypes = _.filter(
      studentTodoFiltersCopy.entityTypes,
      project => project !== "STUDENT_ASSIGNMENT"
    );
  }
  if (
    !_.includes(activeDropdownProjectTypesArr, PROJECT_TYPE_PERSONAL) &&
    !_.includes(activeDropdownProjectTypesArr, PROJECT_TYPE_COMMUNITY) &&
    _.isEqual(currentCurriculumProgramType, CURRICULUM_TYPE_MYP)
  ) {
    studentTodoFiltersCopy.entityTypes = _.filter(
      studentTodoFiltersCopy.entityTypes,
      project => project !== "PROJECT_DEADLINE"
    );
  }

  if (
    !isProjectDeadlineIncluded(activeDropdownProjectTypesArr) &&
    !_.isEqual(currentCurriculumProgramType, CURRICULUM_TYPE_MYP)
  ) {
    studentTodoFiltersCopy.entityTypes = _.filter(
      studentTodoFiltersCopy.entityTypes,
      project =>
        !_.isEqual(MY_CALENDAR_CONSTANTS.PROJECT_DEADLINE_EVENT, project)
    );
  }

  projectGroups = _.filter(projectGroups, project => {
    return (
      _.includes(activeDropdownProjectTypesArr, _.get(project, "subType")) ||
      _.includes(activeDropdownProjectTypesArr, _.get(project, "type"))
    );
  });

  studentTodoFiltersCopy.projectGroupIds = _.map(
    projectGroups,
    project => project.id
  );
  return studentTodoFiltersCopy;
};

export const getTasks = ({ orgId, activeSidebarTab, edges }) => {
  const {
    lastWeekStartDate,
    lastWeekEndDate,
    thisWeekStartDate,
    thisWeekEndDate,
    nextWeekEndDate,
    nextWeekStartDate,
  } = getDateList({ orgId });

  const isStudentAssignmentFeedRevampEnabled = getIsStudentAssignmentFeedRevampEnabled();

  switch (activeSidebarTab) {
    case "UPCOMING": {
      const tasksArr = [];
      const thisWeekTasks = [];
      const nextWeekTasks = [];
      const laterTasks = [];
      _.forEach(edges, edge => {
        if (
          moment(edge.orderDate).isBetween(
            thisWeekStartDate,
            thisWeekEndDate,
            undefined,
            "[]"
          )
        ) {
          thisWeekTasks.push(edge);
        } else if (
          moment(edge.orderDate).isBetween(
            nextWeekStartDate,
            nextWeekEndDate,
            undefined,
            "[]"
          )
        ) {
          nextWeekTasks.push(edge);
        } else {
          laterTasks.push(edge);
        }
      });
      if (thisWeekTasks.length > 0)
        tasksArr.push({
          key: "classRoom:this_week",
          tasks: thisWeekTasks,
        });
      if (nextWeekTasks.length > 0)
        tasksArr.push({
          key: "classRoom:next_week",
          tasks: nextWeekTasks,
        });
      if (laterTasks.length > 0)
        tasksArr.push({
          key: "classRoom:later",
          tasks: laterTasks,
        });
      return {
        tasksArr,
      };
    }
    case "OVERDUE": {
      const tasksArr = [];
      const thisWeekTasks = [];
      const lastWeekTasks = [];
      const earlierTasks = [];
      _.forEach(edges, edge => {
        if (
          moment(edge.orderDate).isBetween(
            thisWeekStartDate,
            thisWeekEndDate,
            undefined,
            "[]"
          )
        ) {
          thisWeekTasks.push(edge);
        } else if (
          moment(edge.orderDate).isBetween(
            lastWeekStartDate,
            lastWeekEndDate,
            undefined,
            "[]"
          )
        ) {
          lastWeekTasks.push(edge);
        } else {
          earlierTasks.push(edge);
        }
      });
      if (thisWeekTasks.length > 0)
        tasksArr.push({
          key: "classRoom:this_week",
          tasks: thisWeekTasks,
        });
      if (lastWeekTasks.length > 0)
        tasksArr.push({
          key: "classRoom:last_week",
          tasks: lastWeekTasks,
        });
      if (earlierTasks.length > 0)
        tasksArr.push({
          key: "classRoom:earlier",
          tasks: earlierTasks,
        });
      return {
        tasksArr,
      };
    }
    case "NODUE": {
      const tasksArr = [
        {
          key: "classRoom:no_due_date",
          tasks: edges,
          showHeading: !isStudentAssignmentFeedRevampEnabled,
        },
      ];
      return {
        tasksArr,
      };
    }
    case "THIS_WEEK": {
      const tasksArr = [{ key: "classRoom:done_this_week", tasks: edges }];
      return {
        tasksArr,
      };
    }
    case "LAST_WEEK": {
      const tasksArr = [{ key: "classRoom:done_last_week", tasks: edges }];
      return {
        tasksArr,
      };
    }
    case "EARLIER": {
      const tasksArr = [{ key: "classRoom:done_earlier", tasks: edges }];
      return {
        tasksArr,
      };
    }
    case "NOT_SUBMITTED": {
      const tasksArr = [
        {
          key: "classRoom:not_submitted",
          tasks: edges,
          showHeading: false,
        },
      ];
      return {
        tasksArr,
      };
    }
    case "SCHEDULED": {
      const tasksArr = [
        {
          key: "classRoom:scheduled",
          tasks: edges,
          showHeading: isStudentAssignmentFeedRevampEnabled ? false : true,
        },
      ];
      return {
        tasksArr,
      };
    }
    default:
      return {
        tasksArr: [],
      };
  }
};

const getCommonFilterCountVariables = ({ orgId }) => {
  const {
    lastWeekStartDate,
    lastWeekEndDate,
    earlierEndDate,
    thisWeekStartDate,
    thisWeekEndDate,
  } = getDateList({ orgId });
  return {
    lastWeekStartDate,
    lastWeekEndDate,
    earlierEndDate,
    thisWeekStartDate,
    thisWeekEndDate,
  };
};

export const getCommonFilterCountVariablesMemoize = _.memoize(
  params => getCommonFilterCountVariables(params),
  params => JSON.stringify(params)
);

export const getDropdownItemTypesArr = ({
  currentCurriculumProgramType,
  isParent = false,
  showHomeScreenWidgets = false,
}) => {
  // For parent, show only STUDENT_ASSIGNMENT
  if (isParent || showHomeScreenWidgets) {
    return ["STUDENT_ASSIGNMENT"];
  }

  switch (currentCurriculumProgramType) {
    case "IB_MYP":
      return ["STUDENT_ASSIGNMENT", "PROJECT_DEADLINE"];
    case "IB_PYP":
    case "UBD":
      return ["STUDENT_ASSIGNMENT"];
  }
  return ["STUDENT_ASSIGNMENT"];
};

export const isTypeDropdownVisible = ({
  currentCurriculumProgramType,
  isParent,
  showHomeScreenWidgets = false,
}) => {
  // For parent, don't show type dropdown
  if (isParent || showHomeScreenWidgets) {
    return false;
  }

  switch (currentCurriculumProgramType) {
    case "IB_MYP":
      return true;
    case "IB_PYP":
      return false;
    case "UBD":
      return false;
  }
};

const getCourseIds = ({ courses }) => {
  return _.map(courses, ({ id }) => id);
};

export const getCourseIdsMemoize = _.memoize(
  params => getCourseIds(params),
  params => JSON.stringify(params)
);

export const getHeaderTabsKey = (overDueCount, isTodoPage) => {
  let label = "all_done";
  if (overDueCount) {
    if (isTodoPage) {
      label = "no_upcoming_have_overdue_todo_page";
    } else {
      label = "no_upcoming_have_overdue";
    }
  }
  return label;
};

export const getEmptyMessage = ({
  activeWidgetTab,
  showFinalMessageForUpcomingDeadlines,
  isParentOrStaff,
  overDueCount,
  isTodoPage = false,
  noAssignmentCreatedYet = false,
  isHeaderTabEmpty = false,
  activeHeaderTab,
}) => {
  if (!isHeaderTabEmpty && !noAssignmentCreatedYet) {
    const HEADER_TABS_EMPTY_STATE_MAPPING_ARRAY = isParentOrStaff
      ? HEADER_TABS_EMPTY_STATE_MSG_MAPPING_V3
      : HEADER_TABS_EMPTY_STATE_MSG_MAPPING_V2;
    if (isActiveTabUpcomingAndTodosEnabled(activeWidgetTab)) {
      if (showFinalMessageForUpcomingDeadlines) {
        return _.get(
          HEADER_TABS_EMPTY_STATE_MAPPING_ARRAY["UPCOMING"],
          getHeaderTabsKey(overDueCount, isTodoPage),
          {}
        );
      } else {
        return _.get(
          HEADER_TABS_EMPTY_STATE_MAPPING_ARRAY["UPCOMING"],
          overDueCount ? "next_30_have_overdue" : "next_30",
          {}
        );
      }
    }
    return _.get(HEADER_TABS_EMPTY_STATE_MAPPING_ARRAY, activeWidgetTab, {});
  } else {
    if (noAssignmentCreatedYet) {
      return {
        emptyMsg: "classRoom:no_assignments_and_deadlines_added_yet_title",
        emptySubMsg: isParentOrStaff
          ? "classRoom:no_assignment_added_yet_subtitle_with_label"
          : "classRoom:no_assignment_added_yet_subtitle",
        emptySubMsgParent: isParentOrStaff
          ? "classRoom:no_assignment_added_yet_subtitle_with_label"
          : "classRoom:no_assignment_added_yet_subtitle",
      };
    } else if (isHeaderTabEmpty) {
      switch (activeHeaderTab) {
        case "TODO":
          return {
            emptyMsg: "classRoom:not_submitted_assignments_empty_state_title",
            emptySubMsg: isParentOrStaff
              ? "classRoom:todo_all_assignment_and_deadlines_submitted_with_label"
              : "classRoom:todo_all_assignment_and_deadlines_submitted",
            emptySubMsgParent: isParentOrStaff
              ? "classRoom:todo_all_assignment_and_deadlines_submitted_with_label"
              : "classRoom:todo_all_assignment_and_deadlines_submitted",
          };

        case "NOT_SUBMITTED":
          return {
            emptyMsg: "classRoom:no_assignment_and_deadlines_missed_yet_title",
            emptySubMsg: isParentOrStaff
              ? "classRoom:not_submitted_all_assignment_and_deadlines_submitted_with_label"
              : "classRoom:not_submitted_all_assignment_and_deadlines_submitted",
          };

        case "COMPLETED":
          return {
            emptyMsg: "classRoom:no_assignment_and_deadlines_turned_in_yet",
            emptySubMsg: "classRoom:once_turned_in_it_will_appear_here",
          };
      }
    } else return {};
  }
};

export const getChildName = userInfo => {
  return _.get(userInfo, "childName");
};

export const getEmptyStateTitleAndSubtitle = ({
  t,
  activeWidgetTab,
  showFinalMessageForUpcomingDeadlines,
  userInfo,
  studentName,
  overDueCount,
  noAssignmentCreatedYet = false,
  isHeaderTabEmpty = false,
  activeHeaderTab = "TODO",
}) => {
  const userType = getUserType();
  const isStaff = userType === "staff";
  const name = isStaff ? studentName : getChildName(userInfo);
  const { emptyMsg, emptySubMsgParent, emptySubMsg } = getEmptyMessage({
    activeWidgetTab,
    showFinalMessageForUpcomingDeadlines,
    isParentOrStaff: _.includes(["parent", "staff"], userType),
    overDueCount,
    noAssignmentCreatedYet,
    isHeaderTabEmpty,
    activeHeaderTab,
  });

  const title = t(emptyMsg, {
    label: name,
  });
  const subtitle = showFinalMessageForUpcomingDeadlines
    ? t(emptySubMsgParent, {
        label: name,
      })
    : t(emptySubMsg, {
        label: name,
      });

  return {
    title,
    subtitle,
  };
};

export const getTabCount = (todosCountMapping, activeHeaderTab) => {
  const tabsArr = HEADER_BASED_TABS_MAPPING[activeHeaderTab] || [];
  return _.reduce(
    tabsArr,
    (sum, tab) => sum + (_.get(todosCountMapping, tab, 0) || 0),
    0
  );
};

export const isTodosWidgetEnable = showClassroomTodo => {
  return ACLStore.can("Common:StudentTodos");
};
export const getStudentTodosCountMappingObject = ({
  studentTasksHomepageCountObj,
}) => {
  const todosCountArray = _.get(
    studentTasksHomepageCountObj,
    "tasks.statusWiseCount",
    []
  );
  const result = _.reduce(
    todosCountArray,
    (todosMap, currTodos) => {
      return {
        ...todosMap,
        [_.get(currTodos, "category")]: _.get(currTodos, "count"),
      };
    },
    {}
  );

  return result;
};

export const getFormattedDateForAssignment = ({ date, t }) => {
  if (_.isEmpty(date)) {
    return "";
  }

  let day;
  let isTodayOrTomorrow = false;
  const isCloseDateToday = checkIsDateSame({
    date1: moment(date),
    date2: moment(),
  });
  const isCloseDateTomorrow = checkIsDateSame({
    date1: moment(date),
    date2: moment().add(1, "day"),
  });

  if (isCloseDateToday) {
    day = t("classRoom:today");
    isTodayOrTomorrow = true;
  } else if (isCloseDateTomorrow) {
    day = t("classRoom:tomorrow");
    isTodayOrTomorrow = true;
  } else {
    day = formatDate({ date: moment(date), format: "ddd" });
  }

  return `${day}, ${getDateTimeV2({
    date: moment(date),
    isTodayOrTomorrow,
  })}`;
};

export const getUpdatedStudentTasks = ({ data }) => {
  const taskEdges = _.get(data, "tasks.edges", []);
  return _.map(taskEdges, edge => {
    const { item } = edge;
    if (item.__typename == "ProjectTaskResponse") {
      return {
        ...edge,
        item: {
          isProjectNewDeadline: true,
          project: item?.mappedProject,
          task: {
            ...item?.task,
          },
        },
      };
    } else return edge;
  });
};
